import {
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	CardHeading,
	Loading,
	SingleSelectField,
	SubmitButton,
	TextField,
	isExternalUrl,
	isSuccess,
	useIsAddonEnabled,
	useToasts,
} from "@dgs/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { countryService } from "../../../shared/api/countryService";
import { CountrySelectField } from "../../../shared/ui/CountrySelectField";
import { ContactFilterSelectField } from "../contactFilter/ContactFilterSelectField";
import { birthdayWidgetService } from "../dashboard/widgets/birthdays/service";
import { contactDatabaseSettingsService } from "../shared/api/contactDatabaseSettingsService";
import { DataFieldOptionResource, IContactDatabaseSettingsResource } from "../shared/types/contactDatabaseSettings";

const useValidate = () => {
	const { t } = useTranslation();

	return (values: IContactDatabaseSettingsResource) => {
		const errors: { [K in keyof IContactDatabaseSettingsResource]?: any } = {};

		if (values.clippingsUrl && !isExternalUrl(values.clippingsUrl)) {
			errors.clippingsUrl = t("The URL must be a valid external URL.");
		}

		return errors;
	};
};

export const ContactDatabaseSettingsPage: FC = () => {
	const { showToast } = useToasts();
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const validate = useValidate();
	const { data, isLoading, refetch } = useQuery({
		queryKey: contactDatabaseSettingsService.keys.getSettings,
		queryFn: () => contactDatabaseSettingsService.getSettings().then((x) => x.data.data),
	});

	const { data: dataFields, isLoading: isDataFieldsLoading } = useQuery({
		queryKey: contactDatabaseSettingsService.keys.getDataFieldOptions,
		queryFn: () => contactDatabaseSettingsService.getDataFieldOptions().then((x) => x.data.data),
	});

	const isPressModuleEnabled = useIsAddonEnabled("pressModule");
	const isContactDatabaseEnabled = useIsAddonEnabled("contactDatabase");

	const onSubmit = useCallback(
		async (values: IContactDatabaseSettingsResource) => {
			const res = await contactDatabaseSettingsService.saveSettings({
				eventClippingProviderContactFilterId: values.eventClippingProviderContactFilter?.id ?? null,
				dateOfBirthDataFieldId: values.dateOfBirthDataField?.id ?? null,
				defaultCountryId: values.defaultCountry?.id ?? null,
				clippingsUrl: values.clippingsUrl,
			});

			if (isSuccess(res)) {
				showToast({
					type: "success",
					title: t("Contact database settings"),
					body: t("Contact database settings were successfully saved."),
				});
				await refetch();
				void queryClient.invalidateQueries({ queryKey: birthdayWidgetService.key });
				void queryClient.invalidateQueries({ queryKey: countryService.defaultCountryKey });
				void queryClient.invalidateQueries({
					predicate: (query) => query.queryKey.includes("clipping-provider-contacts"),
				});
			}
		},
		[queryClient, refetch, showToast, t],
	);

	if (isLoading || isDataFieldsLoading || !data || !dataFields) {
		return <Loading />;
	}

	return (
		<Formik initialValues={data} onSubmit={onSubmit} validate={validate}>
			<Form>
				<Card>
					<CardHeader>
						<CardHeading>{t("Contact database settings")}</CardHeading>
					</CardHeader>
					<CardBody>
						{isContactDatabaseEnabled && isPressModuleEnabled && (
							<ContactFilterSelectField
								name="eventClippingProviderContactFilter"
								label={t("Filter contacts on the event clipping provider list using")}
								types={["system"]}
								clearable={true}
							/>
						)}
						<SingleSelectField
							name="dateOfBirthDataField"
							options={dataFields}
							getLabel={(o: DataFieldOptionResource) => o.label}
							label={t("Date of Birth Datafield")}
						/>
						<TextField name="clippingsUrl" label={t("Clippings URL")} />
						<CountrySelectField name="defaultCountry" label={t("Default country")} />
					</CardBody>
					<CardFooter>
						<SubmitButton>{t("Save")}</SubmitButton>
					</CardFooter>
				</Card>
			</Form>
		</Formik>
	);
};
